/**
 * Utility functions for Epicrisis generation and management
 */

/**
 * Generate an epicrisis draft from the backend
 * @param {string} treatmentId - UUID of the treatment
 * @param {string} patientId - UUID of the patient
 * @param {string} userToken - Authentication token
 * @param {string} language - Language code ('en' or 'da')
 * @param {Object} journalSections - Journal sections from the frontend (optional)
 * @param {Object} appliedDiagnosis - Applied diagnosis object with title and diagnosis fields (optional)
 * @returns {Promise<Object>} Generated epicrisis data
 */
export const generateEpicrisisDraft = async (treatmentId, patientId, userToken, language = 'en', journalSections = null, appliedDiagnosis = null) => {
  if (!treatmentId || !patientId || !userToken) {
    throw new Error('Missing required parameters: treatmentId, patientId, userToken');
  }

  const requestBody = {
    treatment_id: treatmentId,
    patient_id: patientId,
    language: language,
    journal_sections: journalSections || {},
    applied_diagnosis: appliedDiagnosis || {}
  };

  const response = await fetch('https://api.virtus-one.com/journal/epicrisis/generate/', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Token ${userToken}`
    },
    body: JSON.stringify(requestBody)
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.error || 
      `Failed to generate epicrisis: ${response.statusText}`
    );
  }

  return await response.json();
};

/**
 * Transform backend epicrisis response to form data structure
 * @param {Object} epicrisisResponse - Response from /journal/epicrisis/generate/
 * @returns {Object} Form data compatible with ReportDialog
 */
export const transformEpicrisisToFormData = (epicrisisResponse) => {
  if (!epicrisisResponse) return null;

  return {
    referral_reason: epicrisisResponse.referral_reason || '',
    status: epicrisisResponse.status || '',
    gp_suggestion: epicrisisResponse.gp_suggestion || '',
    physiotherapy_diagnosis: epicrisisResponse.physiotherapy_diagnosis || '',
    treatment_summary: epicrisisResponse.treatment_summary || '',
    patient_education: epicrisisResponse.patient_education || '',
    language: epicrisisResponse.language || 'en',
    // Store full payload for reference
    _payload: epicrisisResponse.payload || {}
  };
};

/**
 * Combine generated epicrisis data with existing form data
 * Allows user to override any generated fields
 * @param {Object} generated - Generated data from backend
 * @param {Object} existing - Existing form data (if any)
 * @returns {Object} Merged form data
 */
export const mergeEpicrisisData = (generated, existing = {}) => {
  const merged = transformEpicrisisToFormData(generated);
  
  // If user has existing data, optionally prefer it for certain fields
  // by default, prefer generated data (fresh generation)
  // but allow overrides by excluding fields from merge
  
  return merged;
};

/**
 * Save epicrisis to backend
 * @param {string} epicrisisId - UUID of the epicrisis to update
 * @param {Object} epicrisisData - Form data to save
 * @param {string} userToken - Authentication token
 * @returns {Promise<Object>} Saved epicrisis object
 */
export const saveEpicrisis = async (epicrisisId, epicrisisData, userToken) => {
  if (!epicrisisId || !userToken) {
    throw new Error('Missing required parameters: epicrisisId, userToken');
  }

  const requestBody = {
    referral_reason: epicrisisData.referral_reason || '',
    status: epicrisisData.status || '',
    gp_suggestion: epicrisisData.gp_suggestion || '',
    physiotherapy_diagnosis: epicrisisData.physiotherapy_diagnosis || '',
    treatment_summary: epicrisisData.treatment_summary || '',
    patient_education: epicrisisData.patient_education || '',
    language: epicrisisData.language || 'en'
  };

  const response = await fetch(`https://api.virtus-one.com/journal/epicrisis/${epicrisisId}/save/`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Token ${userToken}`
    },
    body: JSON.stringify(requestBody)
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.error || 
      `Failed to save epicrisis: ${response.statusText}`
    );
  }

  return await response.json();
};

/**
 * List all epicrisis reports for a treatment
 * @param {string} treatmentId - UUID of the treatment
 * @param {string} userToken - Authentication token
 * @returns {Promise<Array>} List of epicrisis objects
 */
export const listEpicrisisReports = async (treatmentId, userToken) => {
  if (!treatmentId || !userToken) {
    throw new Error('Missing required parameters: treatmentId, userToken');
  }

  const response = await fetch(`https://api.virtus-one.com/journal/epicrisis/${treatmentId}/list/`, {
    method: 'GET',
    headers: {
      'Authorization': `Token ${userToken}`
    }
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.error || 
      `Failed to fetch epicrisis list: ${response.statusText}`
    );
  }

  return await response.json();
};

/**
 * Validate epicrisis form data before saving
 * @param {Object} formData - Form data to validate
 * @returns {Object} { isValid: boolean, errors: Array<string> }
 */
export const validateEpicrisisForm = (formData) => {
  const errors = [];

  if (!formData.referral_reason || !formData.referral_reason.trim()) {
    errors.push('Referral reason is required');
  }

  if (!formData.status || !formData.status.trim()) {
    errors.push('Status is required');
  }

  if (!formData.physiotherapy_diagnosis || !formData.physiotherapy_diagnosis.trim()) {
    errors.push('Physiotherapy diagnosis is required');
  }

  if (!formData.patient_education || !formData.patient_education.trim()) {
    errors.push('Patient education is required');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Create a new epicrisis record in the database
 * @param {Object} epicrisisData - Form data to save
 * @param {string} userToken - Authentication token
 * @returns {Promise<Object>} Created epicrisis object
 */
export const createEpicrisis = async (epicrisisData, userToken) => {
  if (!userToken) {
    throw new Error('Missing required parameter: userToken');
  }

  if (!epicrisisData.patient_id || !epicrisisData.treatment_id) {
    throw new Error('Missing required fields: patient_id, treatment_id');
  }

  const requestBody = {
    patient_id: epicrisisData.patient_id,
    treatment_id: epicrisisData.treatment_id,
    referral_reason: epicrisisData.referral_reason || '',
    status: epicrisisData.status || '',
    gp_suggestion: epicrisisData.gp_suggestion || '',
    physiotherapy_diagnosis: epicrisisData.physiotherapy_diagnosis || '',
    treatment_summary: epicrisisData.treatment_summary || '',
    patient_education: epicrisisData.patient_education || '',
    language: epicrisisData.language || 'en'
  };

  const response = await fetch('https://api.virtus-one.com/journal/epicrisis/create/', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Token ${userToken}`
    },
    body: JSON.stringify(requestBody)
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.error || 
      `Failed to create epicrisis: ${response.statusText}`
    );
  }

  return await response.json();
};

/**
 * Create a new status report record in the database
 * @param {Object} statusReportData - Form data to save
 * @param {string} userToken - Authentication token
 * @returns {Promise<Object>} Created status report object
 */
export const createStatusReport = async (statusReportData, userToken) => {
  if (!userToken) {
    throw new Error('Missing required parameter: userToken');
  }

  if (!statusReportData.patient_id || !statusReportData.treatment_id) {
    throw new Error('Missing required fields: patient_id, treatment_id');
  }

  const requestBody = {
    patient_id: statusReportData.patient_id,
    treatment_id: statusReportData.treatment_id,
    metrics_summary: statusReportData.metrics_summary || '',
    baseline_comparison: statusReportData.baseline_comparison || '',
    compliance_report: statusReportData.compliance_report || '',
    physiotherapist_notes: statusReportData.physiotherapist_notes || '',
    language: statusReportData.language || 'en'
  };

  const response = await fetch('https://api.virtus-one.com/journal/status-report/create/', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Token ${userToken}`
    },
    body: JSON.stringify(requestBody)
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.error || 
      `Failed to create status report: ${response.statusText}`
    );
  }

  return await response.json();
};

/**
 * Save status report or epicrisis to database (creates new or updates existing)
 * @param {Object} reportData - Form data to save
 * @param {string} reportType - 'status_report' or 'epicrisis'
 * @param {string} userToken - Authentication token
 * @returns {Promise<Object>} Saved report object
 */
export const saveReport = async (reportData, reportType, userToken) => {
  if (!userToken) {
    throw new Error('Missing required parameter: userToken');
  }

  if (reportType === 'epicrisis') {
    return await createEpicrisis(reportData, userToken);
  } else if (reportType === 'status_report') {
    return await createStatusReport(reportData, userToken);
  } else {
    throw new Error(`Invalid report type: ${reportType}. Must be 'epicrisis' or 'status_report'`);
  }
};
